import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import classNames from 'classnames';
import NormalSelect, { components as selectComponents } from 'react-select';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import Hint from 'components/styleguide/hint/Hint';
import Icon from 'components/styleguide/icon/Icon';

const styles = require('./SelectField.module.scss');

const DropdownIndicator = (props: any) => (
  <selectComponents.DropdownIndicator {...props}>
    <Icon
      name="chevronDown"
      size="sm"
      color={props.isDisabled ? 'neutral-border-disable' : 'neutral-border-dark'}
      aria-hidden="true"
    />
  </selectComponents.DropdownIndicator>
);

const Control = ({ children, ...props }: any) => {
  const iconLeft = props.selectProps?.iconLeft;
  return (
    <selectComponents.Control {...props}>
      {iconLeft && (
        <span className={styles.leftIcon}>
          <Icon
            name={iconLeft}
            size="sm"
            color={props.isDisabled ? 'neutral-border-disable' : 'neutral-border-darker'}
            aria-hidden="true"
          />
        </span>
      )}
      {children}
    </selectComponents.Control>
  );
};

type SelectSize = 'S' | 'M';
type UserType = 'talent' | 'company' | 'neutral';
type SelectType = 'default' | 'async' | 'creatable' | 'async-creatable';

const SIZE_MAP: Record<SelectSize, string> = {
  S: 'small',
  M: 'large',
};

export interface SelectFieldHandle {
  blur: () => void;
  focus: () => void;
  getSelectedOption: () => any;
  getSelectedValue: () => any;
  clearSelectedValue: () => void;
}

export interface SelectFieldProps {
  autoSaveUrl?: string;
  containerClassName?: string;
  disabled?: boolean;
  error?: string;
  hint?: string;
  iconLeft?: string;
  id?: string;
  label?: string;
  name?: string;
  readOnly?: boolean;
  required?: boolean;
  size?: SelectSize;
  testId?: string;
  type?: SelectType;
  context?: UserType;
  [key: string]: any;
}

function HiddenInput({ name, value, isMulti }: { name: string; value: any; isMulti?: boolean }) {
  if (isMulti && Array.isArray(value)) {
    return (
      <>
        {value.map((v: any, i: number) => (
          <input key={i} type="hidden" name={`${name}[]`} value={v?.value ?? ''} />
        ))}
      </>
    );
  }

  return <input type="hidden" name={name} value={value?.value ?? ''} />;
}

function getComponent(type: SelectType) {
  switch (type) {
    case 'async':
      return AsyncSelect;
    case 'async-creatable':
      return AsyncCreatableSelect;
    case 'creatable':
      return CreatableSelect;
    default:
      return NormalSelect;
  }
}

function getInternalValue(selectRef: React.RefObject<any>, type: SelectType) {
  const component = selectRef.current;
  if (!component) return undefined;

  switch (type) {
    case 'async':
    case 'async-creatable':
      return component.select?.state?.value;
    default:
      return component.state?.value;
  }
}

const SelectField = forwardRef<SelectFieldHandle, SelectFieldProps>(function SelectField({
  autoSaveUrl,
  containerClassName,
  disabled,
  error,
  hint,
  iconLeft,
  id,
  label,
  name,
  readOnly,
  required,
  size = 'M',
  testId,
  type = 'default',
  context = 'neutral',
  ...reactSelectProps
}, ref) {
  const { autoSaveError, save } = useAutoSave({
    url: autoSaveUrl || '',
    name: name || '',
  });
  const selectRef = useRef<any>(null);
  const [internalValue, setInternalValue] = useState(reactSelectProps.value ?? null);
  const [displayError, setDisplayError] = useState(error || '');
  const isControlled = reactSelectProps.onChange !== undefined;

  useEffect(() => { if (autoSaveError) setDisplayError(autoSaveError); }, [autoSaveError]);
  const sizeClass = SIZE_MAP[size];
  const selectId = id || `select-field-${Math.random().toString(36).slice(2)}`;

  useEffect(() => {
    setDisplayError(error || '');
  }, [error]);

  useImperativeHandle(ref, () => ({
    blur: () => selectRef.current?.blur(),
    focus: () => selectRef.current?.focus(),
    getSelectedOption: () => getInternalValue(selectRef, type),
    getSelectedValue: () => {
      const option = getInternalValue(selectRef, type);
      return option ? option.value : undefined;
    },
    clearSelectedValue: () => {
      const { isMulti } = reactSelectProps;
      const value = isMulti ? [] : null;
      let component = selectRef.current;
      if (!component) return;

      switch (type) {
        case 'async':
        case 'async-creatable':
          component = component.select;
          break;
      }

      component?.setState({ value });
    },
  }));

  if (readOnly) {
    return <div className={styles.readOnly}>{reactSelectProps.value?.label}</div>;
  }

  const Component = getComponent(type);
  const hasMessage = Boolean(displayError) || Boolean(hint);
  const messageId = hasMessage ? `${selectId}-message` : undefined;

  const containerClasses = classNames(styles.container, styles[context], {
    [styles[sizeClass]]: sizeClass,
    [styles.error]: Boolean(displayError),
    [styles.disabled]: disabled,
  });

  const wrapperClasses = classNames(styles.wrapper, containerClassName);

  return (
    <div className={wrapperClasses} data-testid={testId}>
      {label && (
        <div className={styles.labelContainer}>
          <label htmlFor={selectId} className={styles.label}>{label}</label>
          {required && <span className={styles.required} aria-hidden="true">*</span>}
        </div>
      )}

      <label
        htmlFor={selectId}
        className={containerClasses}
        aria-invalid={Boolean(displayError)}
        aria-describedby={messageId}
        aria-required={required || undefined}
      >
        <Component
          inputId={selectId}
          classNamePrefix="sf"
          isDisabled={disabled}
          ref={selectRef}
          iconLeft={iconLeft}
          {...reactSelectProps}
          components={{ DropdownIndicator, Control, ...(reactSelectProps.components || {}) }}
          {...(!isControlled && {
            value: internalValue,
            onChange: (newValue: any) => {
              setInternalValue(newValue);
              setDisplayError('');
              if (autoSaveUrl) save(newValue?.value ?? '');
            },
          })}
          {...(isControlled && reactSelectProps.onChange && {
            onChange: (...args: any[]) => {
              setDisplayError('');
              reactSelectProps.onChange(...args);
            },
          })}
        />
      </label>

      {name && (
        <HiddenInput name={name} value={isControlled ? reactSelectProps.value : internalValue} isMulti={reactSelectProps.isMulti} />
      )}

      {hasMessage && (
        <Hint
          id={messageId}
          variant={displayError ? 'error' : 'neutral'}
          message={displayError || hint}
        />
      )}
    </div>
  );
});

export default SelectField;
